// BE#366 — shared sizing policy for the `agent-vision` media derivative.
//
// Both engines (baileys pod via `sharp`, chrome extension via `canvas`) downsize an inbound
// image to a small JPEG for Gemini vision. Keeping the target math HERE (one dependency-free
// module both engines import) is what guarantees the two resizers produce equivalent output —
// identical target dimensions + the same quality ladder + the same byte ceiling.
//
// Sizing rationale: Gemini 2.5 tiles images at 768×768 (~258 tokens/tile); detail beyond the
// tile grid is unused. A 1024px long edge = 1–2 tiles, keeps headroom for text-in-image, and a
// JPEG at q≈82 lands ~150–350KB — well under any inline limit, so no cap/GCS fallback is needed.

/** Max long-edge (px) for the downsized derivative. */
export const AGENT_VISION_MAX_EDGE = 1024;
/** Floor long-edge (px) — the last step of the adaptive ladder if the byte ceiling is still exceeded. */
export const AGENT_VISION_MIN_EDGE = 768;
/** Primary JPEG quality. */
export const AGENT_VISION_JPEG_QUALITY = 82;
/** Soft byte ceiling for the encoded derivative; the ladder steps down until under this. */
export const AGENT_VISION_TARGET_MAX_BYTES = 400_000;
/** Output mime — everything (HEIC/WebP/PNG/GIF-first-frame) normalizes to baseline JPEG. */
export const AGENT_VISION_OUTPUT_MIME = 'image/jpeg';

/**
 * Aspect-preserving fit into a `maxEdge`×`maxEdge` box, never enlarging.
 * Returns integer pixel dimensions. Zero/invalid inputs pass through as-is (caller no-ops).
 */
export function computeFitDimensions(
    width: number,
    height: number,
    maxEdge: number = AGENT_VISION_MAX_EDGE,
): { width: number; height: number } {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return { width: Math.max(0, Math.round(width) || 0), height: Math.max(0, Math.round(height) || 0) };
    }
    const longEdge = Math.max(width, height);
    if (longEdge <= maxEdge) return { width: Math.round(width), height: Math.round(height) };
    const scale = maxEdge / longEdge;
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * The adaptive encode ladder: try each (maxEdge, quality) in order and keep the first result
 * under the byte ceiling; if none fits, use the LAST (smallest) — the derivative is always
 * bounded, so there is never a cap/fallback branch. Both engines walk this same ladder.
 */
export const AGENT_VISION_ENCODE_LADDER: ReadonlyArray<{ maxEdge: number; quality: number }> = [
    { maxEdge: AGENT_VISION_MAX_EDGE, quality: AGENT_VISION_JPEG_QUALITY }, // 1024 / q82
    { maxEdge: AGENT_VISION_MAX_EDGE, quality: 72 },                        // 1024 / q72
    { maxEdge: AGENT_VISION_MIN_EDGE, quality: 72 },                        // 768  / q72
];
